// src/pages/HeyHotelLoginPage.tsx
// Handles HeyHotelLoginURL/{hotelCode} and HeyHotelLoginURL/{hotelCode}/{reservationId}/{profileId}
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/chat';
import { isGuestProfileIncomplete } from '../shared/validators';

interface DeepLinkGuest {
  profileId: string;
  firstName: string;
  lastName: string;
}

const HeyHotelLoginPage: React.FC = () => {
  const { hotelCode, reservationId, profileId } = useParams<{
    hotelCode: string;
    reservationId?: string;
    profileId?: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectGuestFromDeepLink } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hotelId, setHotelId] = useState('');
  const [resolvedReservationId, setResolvedReservationId] = useState('');
  const [guests, setGuests] = useState<DeepLinkGuest[]>([]);
  const [reservation, setReservation] = useState<any>(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const redirectToHotelLogin = useCallback(
    (code: string, resolvedHotelId?: string) => {
      const params = new URLSearchParams({ hotelCode: code });
      if (resolvedHotelId) {
        params.append('hotelId', resolvedHotelId);
      }
      navigate(`/login?${params.toString()}`, { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    if (!hotelCode) {
      setError(t('deepLink.invalidLink'));
      setLoading(false);
      return;
    }

    const resolve = async () => {
      try {
        setLoading(true);
        setError('');

        const params = new URLSearchParams({ hotelCode });
        if (reservationId) params.append('reservationId', reservationId);
        if (profileId) params.append('profileId', profileId);

        const response = await api.get(`/auth/deep-link/resolve?${params.toString()}`);
        const data = response.data;

        if (data.type === 'hotel_login') {
          redirectToHotelLogin(data.hotelCode || hotelCode, data.hotelId);
          return;
        }

        setHotelId(data.hotelId);
        setResolvedReservationId(data.reservationId);
        setGuests(data.guests || []);
        setReservation(data.reservation);
        setLoading(false);
      } catch (err: any) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          t('deepLink.resolveFailed');
        setError(typeof message === 'string' ? message : t('deepLink.resolveFailed'));
        setLoading(false);
      }
    };

    resolve();
  }, [hotelCode, reservationId, profileId, redirectToHotelLogin, t]);

  const handleGuestSelection = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedProfileId) {
      setError(t('login.pleaseSelectGuest'));
      return;
    }

    setSubmitting(true);

    try {
      const result = await selectGuestFromDeepLink(hotelId, resolvedReservationId, selectedProfileId);

      if (result.user?.role === UserRole.GUEST && isGuestProfileIncomplete(result.user)) {
        const params = new URLSearchParams({
          hotelId,
          reservationId: resolvedReservationId,
          profileId: selectedProfileId,
          verify: '1',
          redirectToChat: 'true',
        });
        const selectedGuest = guests.find((g) => g.profileId === selectedProfileId);
        if (selectedGuest?.lastName) {
          params.append('surname', selectedGuest.lastName);
        }
        navigate(`/complete-guest-info?${params.toString()}`);
        return;
      }

      try {
        const chatsResponse = await api.get('/chats');
        const chats = chatsResponse.data;
        if (chats && chats.length > 0) {
          navigate(`/dashboard/chat/${chats[0].id}`);
        } else {
          navigate('/dashboard');
        }
      } catch {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('login.guestSelectionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6">{t('deepLink.loading')}</Typography>
        </Paper>
      </Container>
    );
  }

  if (error && guests.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" fullWidth onClick={() => redirectToHotelLogin(hotelCode || '')}>
            {t('deepLink.goToLogin')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/heyhotelai-logo.png"
            alt="HeyHotel.AI"
            sx={{ height: 50, width: 'auto', objectFit: 'contain', mb: 2 }}
          />
          <Typography variant="h5" component="h1" gutterBottom>
            {t('deepLink.selectYourProfile')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('deepLink.selectYourProfileDesc')}
          </Typography>
        </Box>

        {reservation && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>{t('login.reservation')}:</strong>{' '}
              {reservation.confirmationNumber || reservation.reservationID || resolvedReservationId}
            </Typography>
            {(reservation.roomNumber || reservation.RoomNumber) && (
              <Typography variant="body2">
                <strong>{t('login.room')}:</strong> {reservation.roomNumber || reservation.RoomNumber}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleGuestSelection}>
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
            {t('login.multipleGuests')}
          </FormLabel>
          <RadioGroup
            value={selectedProfileId}
            onChange={(e) => setSelectedProfileId(e.target.value)}
          >
            {guests.map((guest) => (
              <Card
                key={guest.profileId}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  borderColor: selectedProfileId === guest.profileId ? '#00AEEF' : 'divider',
                  borderWidth: selectedProfileId === guest.profileId ? 2 : 1,
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedProfileId(guest.profileId)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <FormControlLabel
                    value={guest.profileId}
                    control={<Radio />}
                    label={`${guest.firstName} ${guest.lastName}`.trim()}
                    sx={{ m: 0, width: '100%' }}
                  />
                </CardContent>
              </Card>
            ))}
          </RadioGroup>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting || !selectedProfileId}
            sx={{
              mt: 3,
              py: 1.5,
              backgroundColor: '#00AEEF',
              '&:hover': { backgroundColor: '#0095cf' },
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : t('login.continue')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default HeyHotelLoginPage;
